import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/useAuth'
import { useTranslation } from 'react-i18next'
import { getDashboardPath } from '@/utils/getDashboardPath'

interface FooterProps {
  isChatPage: boolean;
}

const Footer: React.FC<FooterProps> = ({ isChatPage }) => {
  const location = useLocation()
  const { t } = useTranslation()
  const { user, userProfile } = useAuth()

  // Use the prop from App.tsx to determine visibility
  if (isChatPage) {
    return null;
  }

  // Fallback logic for non-chat pages
  // Show footer on home page and landing-style signup pages
  // Fallback logic for non-chat pages
  // Show footer on home page and landing-style signup pages
  const landingPages = ['/', '/landing', '/homeschool', '/prep', '/facility-signup', '/family-signup', '/privacy', '/terms', '/security', '/help', '/contact', '/sponsor', '/elite', '/elite-academy', '/ngo', '/ngo/apply', '/verification']
  const shouldShowFooter = landingPages.includes(location.pathname)

  if (!shouldShowFooter) {
    return null
  }

  const isHomeschool = userProfile?.facility_type === 'homeschool' || userProfile?.facility_type === 'family_edu'
  const isHomeschoolStudent = isHomeschool && (userProfile?.facility_role === 'member' || userProfile?.facility_role === 'student')
  const dashboardPath = user ? getDashboardPath(userProfile, user?.user_metadata) : '/auth/login'
  const subscriptionPath = isHomeschool ? '/homeschool/subscription' : '/subscription'

  // If visitor is logged out (guest), render public marketing links instead of auth-walled endpoints
  const quickLinks = user ? [
    { to: dashboardPath, label: t('navigation.dashboard') },
    { to: '/sponsor', label: "Sponsor a Child", premium: true },
    { to: '/ngo', label: "NGO / Partner Program" },
    { to: '/settings', label: t('navigation.settings') },
    ...(!isHomeschoolStudent ? [{ to: subscriptionPath, label: t('navigation.subscription') }] : []),
    { to: '/facility-signup', label: t('layout.footer.facilityService'), premium: true },
    { to: '/affiliates', label: "Affiliate Program" },
  ] : [
    { to: '/elite', label: "Curriculum (Elite)", premium: true },
    { to: '/homeschool', label: "Homeschool" },
    { to: '/prep', label: "Free Prep" },
    { to: '/sponsor', label: "Sponsor a Child", premium: true },
    { to: '/ngo', label: "NGO / Partner Program" },
    { to: '/affiliates', label: "Affiliate Program" },
  ]

  return (
    <footer className="relative bg-slate-900 overflow-hidden pt-20 pb-12 border-t border-white/5">
      <div className="absolute top-0 left-0 w-full h-full bg-mesh opacity-5 pointer-events-none" />
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-16 mb-16">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2 space-y-6">
            <div className="flex items-center space-x-3 group cursor-pointer">
              <div className="w-12 h-12 overflow-hidden">
                <img src="/icons/blue-sage-logo.png" alt="Still Waters Academy" className="w-full h-full object-contain" />
              </div>
              <span className="text-2xl sm:text-3xl font-black text-white tracking-tighter">Still Waters Academy</span>
            </div>
            <p className="text-xl text-slate-400 max-w-md leading-relaxed font-medium">
              {t('layout.footer.tagline')}
            </p>
            <div className="pt-4 flex flex-col gap-2">
              <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">
                Still Waters LLC &bull; Wyoming, USA
              </p>
              <a href="mailto:support@stillwatersacademy.com" className="text-emerald-400 hover:text-emerald-300 font-bold transition-colors">
                support@stillwatersacademy.com
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6">{t('layout.footer.quickLinks')}</h3>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className={cn(
                      "text-lg font-bold transition-all hover:translate-x-1 inline-block",
                      link.premium ? "text-emerald-400 hover:text-emerald-300" : "text-slate-300 hover:text-white"
                    )}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6">{t('layout.footer.support')}</h3>
            <ul className="space-y-4">
              {[
                { to: '/help', label: t('layout.footer.helpCenter') },
                { to: '/security', label: t('layout.footer.securityData') },
                { to: '/privacy', label: t('layout.footer.privacyPolicy') },
                { to: '/verification', label: 'Verification' },
                { to: '/terms', label: t('layout.footer.termsOfService') },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-lg text-slate-300 hover:text-white font-bold transition-all hover:translate-x-1 inline-block">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6 text-slate-400 font-bold">
          <p className="text-lg">&copy; {new Date().getFullYear()} Still Waters Academy. {t('layout.footer.allRightsReserved')}</p>
          <div className="flex items-center gap-8">
            <span className="hover:text-emerald-400 cursor-pointer transition-colors">{t('layout.footer.privacyFirst')}</span>
            <span className="hover:text-emerald-400 cursor-pointer transition-colors">{t('layout.footer.secureData')}</span>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
